import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import '~/styles/landing.css';
import { ApiError, SSO_LOGIN_URL } from '~/lib/api';
import { useAuthConfig, useDevLogin, useMe } from '~/hooks/use-auth';
import type { AuthErrorCode, AuthMe } from '~shared/schemas';

/**
 * Вход участника для его страниц — лендинга и положения об акции (landing «Лендинг только для вошедших»,
 * promo-rules «Положение только для вошедших»): ждём сессию; участник → `children`; NL не ответил →
 * «недоступно»; без сессии → ошибка входа из `auth_error`, dev-вход или переход на вход через NL.
 * AICODE-NOTE: при включённом dev-входе (только dev — в production сервер с секретом не стартует)
 * автоматического перехода нет: иначе тестировщик не доберётся до формы входа по ID.
 */
/** AICODE-NOTE: метка последнего автоматического перехода на вход. Переход — только без `auth_error`
 *  (любая ошибка входа возвращает с ним) и не чаще раза в 30 с: если NL вернул бы на лендинг без
 *  сессии и без кода ошибки, лендинг не зациклит браузер, а покажет ошибку SSO_UNAVAILABLE. */
const REDIRECT_MARK = 'ny_sso_redirect_at';
const REDIRECT_COOLDOWN_MS = 30_000;

export function ParticipantGate({ children }: { children: (participant: AuthMe) => ReactNode }) {
  const me = useMe();
  const authConfig = useAuthConfig();
  const [params] = useSearchParams();
  const authError = params.get('auth_error') as AuthErrorCode | null;

  const signedIn = Boolean(me.data);
  const canRedirect =
    !me.isPending && !me.isError && !signedIn && !authError && authConfig.data?.ssoEnabled === true && !authConfig.data.devLoginEnabled;
  const [redirectBlocked] = useState(() => Date.now() - Number(sessionStorage.getItem(REDIRECT_MARK) ?? 0) < REDIRECT_COOLDOWN_MS);

  useEffect(() => {
    if (signedIn) sessionStorage.removeItem(REDIRECT_MARK);
  }, [signedIn]);
  useEffect(() => {
    if (!canRedirect || redirectBlocked) return;
    sessionStorage.setItem(REDIRECT_MARK, String(Date.now()));
    // replace: «Назад» со страницы NL не возвращает на пустой экран, который снова увёл бы на вход.
    window.location.replace(SSO_LOGIN_URL);
  }, [canRedirect, redirectBlocked]);

  if (me.isPending || authConfig.isPending) return <ScreenLoading />;
  if (me.isError || authConfig.isError) return <ScreenUnavailable onRetry={() => void Promise.all([me.refetch(), authConfig.refetch()])} />;
  if (me.data) return children(me.data);
  if (authError) return <ScreenAuthError code={authError} devLogin={authConfig.data.devLoginEnabled} />;
  if (authConfig.data.devLoginEnabled) return <ScreenDevLogin ssoEnabled={authConfig.data.ssoEnabled} />;
  if (!authConfig.data.ssoEnabled) return <ScreenAuthError code="SSO_UNAVAILABLE" />;
  if (redirectBlocked) return <ScreenAuthError code="SSO_UNAVAILABLE" />;
  return <ScreenLoading />;
}

function ScreenLoading() {
  const { t } = useTranslation('auth');
  return (
    <main className="ny-screen" aria-busy="true">
      <div className="spin" role="status" aria-label={t('screens.loading')} />
    </main>
  );
}

function ScreenUnavailable({ onRetry }: { onRetry: () => void }) {
  const { t } = useTranslation('auth');
  return (
    <main className="ny-screen">
      <div className="card">
        <h1>{t('screens.unavailableTitle')}</h1>
        <p>{t('screens.unavailableText')}</p>
        <button type="button" className="act" onClick={onRetry}>
          {t('screens.retry')}
        </button>
      </div>
    </main>
  );
}

const DEV_LOGIN_ERRORS = {
  403: 'devLogin.wrongSecret',
  422: 'devLogin.unknownParticipant',
  503: 'devLogin.lakeUnavailable',
} as const;

function devLoginErrorKey(error: unknown) {
  if (!(error instanceof ApiError)) return 'devLogin.failed';
  return DEV_LOGIN_ERRORS[error.status as keyof typeof DEV_LOGIN_ERRORS] ?? 'devLogin.failed';
}

function ScreenAuthError({ code, devLogin = false }: { code: AuthErrorCode; devLogin?: boolean }) {
  const { t } = useTranslation('auth');
  return (
    <main className="ny-screen" role="alert">
      <div className="card">
        <h1>{t('screens.errorTitle')}</h1>
        <p>{t(`errors.${code}`)}</p>
        <a className="act" href={SSO_LOGIN_URL}>
          {t('screens.login')}
        </a>
        {devLogin && <DevLoginPanel />}
      </div>
    </main>
  );
}

function ScreenDevLogin({ ssoEnabled }: { ssoEnabled: boolean }) {
  const { t } = useTranslation('auth');
  return (
    <main className="ny-screen">
      <div className="card">
        {ssoEnabled && (
          <a className="act" href={SSO_LOGIN_URL}>
            {t('screens.login')}
          </a>
        )}
        <DevLoginPanel />
      </div>
    </main>
  );
}

function DevLoginPanel() {
  const { t } = useTranslation('auth');
  const devLogin = useDevLogin();
  const [participantId, setParticipantId] = useState('');
  const [secret, setSecret] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    devLogin.mutate({ participantId, secret });
  };

  return (
    <form onSubmit={submit} className="mx-auto max-w-md space-y-3 border-t border-dashed border-[var(--ny-gold,#f2c46d)]/50 bg-[#0b1630] p-4">
      <p className="text-xs uppercase tracking-wider text-[var(--ny-gold,#f2c46d)]">{t('devLogin.label')}</p>
      <input
        value={participantId}
        onChange={(e) => setParticipantId(e.target.value)}
        placeholder={t('devLogin.participantIdPlaceholder')}
        className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm text-white placeholder:text-white/40"
      />
      <input
        type="password"
        value={secret}
        onChange={(e) => setSecret(e.target.value)}
        placeholder={t('devLogin.secretPlaceholder')}
        className="w-full rounded-lg bg-white/10 px-3 py-2 text-sm text-white placeholder:text-white/40"
      />
      {devLogin.isError && (
        <p className="text-xs text-[var(--ny-berry,#e2475b)]">
          {t(devLoginErrorKey(devLogin.error))}
        </p>
      )}
      <button
        type="submit"
        disabled={devLogin.isPending || !participantId || !secret}
        className="h-10 w-full rounded-lg bg-[var(--ny-gold,#f2c46d)] text-sm font-medium text-[#0b1630] disabled:opacity-50"
      >
        {t('devLogin.submit')}
      </button>
    </form>
  );
}
